import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import TopEmployers from "@/components/TopEmployers";
import { JobContextBlock, SmartInterlinks } from "@/components/JobSemanticBlocks";
import useNoindexOnNotFound from "@/hooks/useNoindexOnNotFound";
import { Briefcase, MapPin, IndianRupee, Clock, ExternalLink } from "lucide-react";

const SITE_URL = "https://hireds.in";

// -----------------------------------------------------------
// PRIVATE JOBS INDEX  (/private-jobs)
// -----------------------------------------------------------
export function PrivateJobsIndex() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sp] = useSearchParams();
  const sector = sp.get("sector") || "";
  const listing = sp.get("listing") || "";

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("limit", "30");
    if (sector) params.set("sector", sector);
    if (listing) params.set("listing", listing);
    api.get(`/private-jobs?${params.toString()}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
    api.get(`/private-companies${sector ? `?sector=${sector}` : ""}`).then((r) => {
      setCompanies(r.data.items || []);
      setSectors(r.data.sectors || []);
    });
  }, [sector, listing]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" data-testid="private-jobs-index">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Private Jobs" }]} />
      <header className="mt-4 mb-6">
        <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Corporate India Careers</div>
        <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mt-1 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Private Sector Jobs in India 2026
        </h1>
        <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">
          {total}+ live openings from India&apos;s top private employers — Reliance, TCS, Infosys, HDFC Bank, ICICI Bank, Tata Motors, Airtel, Zerodha, Dream11, NSE, Serum Institute and more. Refreshed daily at 12:00 AM IST.
        </p>

        {/* Sector filter — SEO-friendly native anchors */}
        <nav aria-label="Filter by sector" className="mt-5 flex flex-wrap gap-2" data-testid="private-sector-filter">
          <Link
            to="/private-jobs"
            data-testid="private-filter-all"
            className={`inline-flex items-center h-9 px-3 rounded-full text-xs font-semibold ${!sector ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-300 hover:border-orange-400"}`}
          >
            All Sectors
          </Link>
          {sectors.map((s) => (
            <Link
              key={s.slug}
              to={`/private-jobs?sector=${s.slug}`}
              data-testid={`private-filter-${s.slug}`}
              className={`inline-flex items-center h-9 px-3 rounded-full text-xs font-semibold ${sector === s.slug ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-300 hover:border-orange-400"}`}
            >
              {s.name}
            </Link>
          ))}
        </nav>
      </header>

      <TopEmployers companies={companies} limit={12} />

      <section aria-labelledby="latest-private-h2" className="mt-8" data-testid="private-jobs-list">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Hot Openings</div>
            <h2 id="latest-private-h2" className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              Latest Private Sector Openings
            </h2>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="text-slate-500 py-8">No private listings match your filters. <Link to="/private-jobs" className="text-orange-600 font-semibold">Reset →</Link></p>
        ) : (
          <ul className="space-y-3" role="list">
            {items.map((j) => (
              <li key={j.id}>
                <PrivateJobRow job={j} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// -----------------------------------------------------------
// PRIVATE COMPANY DETAIL  (/private-jobs/:companySlug)
// -----------------------------------------------------------
export function PrivateCompanyDetail() {
  const { companySlug } = useParams();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/private-companies/${companySlug}`)
      .then((r) => setCompany(r.data))
      .catch(() => setError(true));
  }, [companySlug]);

  useNoindexOnNotFound(error);

  // Inject Organization JSON-LD once company loads
  useEffect(() => {
    if (!company) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld-org", "1");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: company.name,
      url: company.website,
      sameAs: company.same_as,
      foundingDate: company.founded ? String(company.founded) : undefined,
      address: company.hq ? { "@type": "PostalAddress", addressLocality: company.hq, addressCountry: "IN" } : undefined,
      description: company.description,
    });
    document.head.appendChild(script);
    return () => {
      document.querySelectorAll('script[data-jsonld-org]').forEach((s) => s.remove());
    };
  }, [company]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold text-slate-900">Employer not found</h1>
        <p className="mt-2 text-slate-600">Browse all <Link to="/private-jobs" className="text-orange-600 font-semibold">private-sector openings →</Link></p>
      </div>
    );
  }

  if (!company) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Loading employer profile…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" data-testid="private-company-detail">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Private Jobs", to: "/private-jobs" },
          { label: company.short_name || company.name },
        ]}
      />

      <header
        className="mt-4 mb-8 rounded-xl overflow-hidden p-6 md:p-10"
        style={{ background: company.brand_bg || "#0f172a", color: company.brand_color || "#fff" }}
        data-testid="company-hero"
      >
        <div className="text-xs uppercase tracking-wider font-bold opacity-90">
          {(company.sector || "").replace(/-/g, " ")} {company.listing ? ` · ${company.listing}` : ""}
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold mt-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {company.name} Careers &amp; Jobs 2026
        </h1>
        <p className="mt-3 text-sm md:text-base opacity-90 leading-relaxed max-w-3xl">
          {company.description}
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-xs md:text-sm">
          {company.hq && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 opacity-80" />{company.hq}</span>}
          {company.founded && <span>Founded {company.founded}</span>}
          <span>{company.total_jobs || 0} live openings</span>
        </div>
        {company.careers_url && (
          <a
            href={company.careers_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="company-careers-link"
            className="mt-5 inline-flex items-center gap-2 bg-white/95 text-slate-900 hover:bg-white px-4 py-2 rounded-md text-sm font-semibold"
          >
            Visit official careers page <ExternalLink className="w-4 h-4" />
          </a>
        )}
        <Link
          to={`/private-jobs/${company.slug}/salaries`}
          data-testid="company-salaries-link"
          className="mt-5 ml-2 inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 border border-white/40 px-4 py-2 rounded-md text-sm font-semibold"
        >
          View {company.short_name || company.name} salaries →
        </Link>
      </header>

      <section aria-labelledby="company-openings-h2" data-testid="company-jobs-list">
        <h2 id="company-openings-h2" className="text-2xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Live openings at {company.short_name || company.name}
        </h2>
        {Array.isArray(company.jobs) && company.jobs.length ? (
          <ul className="space-y-3" role="list">
            {company.jobs.map((j) => (
              <li key={j.id}><PrivateJobRow job={{ ...j, company }} /></li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500">No live openings today. Check back tomorrow or <Link to="/private-jobs" className="text-orange-600 font-semibold">browse other employers →</Link></p>
        )}
      </section>

      <p className="mt-10 text-xs text-slate-500 border-t border-slate-200 pt-4">
        hireds.in is <strong>not affiliated</strong> with {company.name}. Job listings are curated from public sources and linked to the official application URL. Always verify details on the company&apos;s official careers page before applying.
      </p>
    </div>
  );
}

// -----------------------------------------------------------
// PRIVATE JOB DETAIL  (/private-jobs/:companySlug/:jobSlug)
// -----------------------------------------------------------
export function PrivateJobDetail() {
  const { companySlug, jobSlug } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/private-jobs/${companySlug}/${jobSlug}`)
      .then((r) => setJob(r.data))
      .catch(() => setError(true));
  }, [companySlug, jobSlug]);

  useNoindexOnNotFound(error);

  // JobPosting JSON-LD
  useEffect(() => {
    if (!job) return;
    const c = job.company || {};
    const datePosted = (job.posted_date || "").split("T")[0] || new Date().toISOString().split("T")[0];
    const validThrough = job.last_date || datePosted;
    const jp = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: `<p>${(job.description || job.title).replace(/\n/g, "<br/>")}</p>`,
      identifier: { "@type": "PropertyValue", name: "hireds.in", value: job.id || job.slug },
      datePosted,
      validThrough,
      employmentType: (job.job_type || "FULL_TIME").toUpperCase().replace(/-/g, "_"),
      hiringOrganization: {
        "@type": "Organization",
        name: c.name || job.company_slug,
        sameAs: c.same_as || c.website || undefined,
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: (job.location || "").split(",")[0].trim() || undefined,
          addressRegion: (job.location || "").split(",").slice(1).join(",").trim() || undefined,
          addressCountry: "IN",
        },
      },
      baseSalary: (job.salary_min || job.salary_max) ? {
        "@type": "MonetaryAmount",
        currency: "INR",
        value: {
          "@type": "QuantitativeValue",
          minValue: job.salary_min || 0,
          maxValue: job.salary_max || job.salary_min || 0,
          unitText: "MONTH",
        },
      } : undefined,
      educationRequirements: job.qualification ? {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: job.qualification,
      } : undefined,
      experienceRequirements: job.experience || undefined,
      applicantLocationRequirements: { "@type": "Country", name: "India" },
      url: `${SITE_URL}/private-jobs/${job.company_slug}/${job.slug}`,
      directApply: Boolean(job.apply_url),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld-jobposting", "1");
    script.text = JSON.stringify(jp);
    document.head.appendChild(script);
    return () => {
      document.querySelectorAll('script[data-jsonld-jobposting]').forEach((s) => s.remove());
    };
  }, [job]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold text-slate-900">This opening is no longer active</h1>
        <p className="mt-2 text-slate-600">Browse other openings at <Link to={`/private-jobs/${companySlug}`} className="text-orange-600 font-semibold">this employer →</Link></p>
      </div>
    );
  }

  if (!job) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Loading job…</div>;

  const c = job.company || {};

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6" data-testid="private-job-detail">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Private Jobs", to: "/private-jobs" },
          { label: c.short_name || c.name || companySlug, to: `/private-jobs/${companySlug}` },
          { label: job.title },
        ]}
      />

      <header className="mt-4 mb-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-orange-600">
          <Briefcase className="w-3.5 h-3.5" />
          <Link to={`/private-jobs/${companySlug}`} className="hover:underline">
            {c.name || job.company_slug}
          </Link>
          {c.listing && <span className="text-slate-500">· {c.listing}</span>}
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {job.title}
        </h1>
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-700">
          {job.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-slate-400" />{job.location}</span>}
          {job.experience && <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4 text-slate-400" />{job.experience}</span>}
          {job.salary_min ? (
            <span className="inline-flex items-center gap-1"><IndianRupee className="w-4 h-4 text-slate-400" />{job.salary_min.toLocaleString("en-IN")}–{(job.salary_max || job.salary_min).toLocaleString("en-IN")}/mo</span>
          ) : null}
          {job.qualification && <span className="text-slate-600">{job.qualification}</span>}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Posted: <time dateTime={(job.posted_date || "").slice(0, 10)}>{fmtDate(job.posted_date)}</time>
          {job.last_date && <> · <strong className="text-orange-700">Apply by {fmtDate(job.last_date)}</strong></>}
        </p>

        {job.apply_url && (
          <a
            href={job.apply_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="private-job-apply"
            className="mt-5 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-3 rounded-md"
          >
            Apply on {c.short_name || c.name || "official portal"} <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </header>

      <section className="mt-6 prose prose-slate max-w-none">
        <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Role Overview</h2>
        <p className="text-slate-700 leading-relaxed">{job.description}</p>
      </section>

      {/* Dynamic context paragraph — unique per private-job page */}
      <JobContextBlock job={job} />

      {Array.isArray(job.responsibilities) && job.responsibilities.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Key Responsibilities</h2>
          <ul className="list-disc pl-6 space-y-1 text-slate-700">
            {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </section>
      )}

      {Array.isArray(job.skills_required) && job.skills_required.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Skills Required</h2>
          <ul className="flex flex-wrap gap-2">
            {job.skills_required.map((s, i) => (
              <li key={i} className="inline-flex items-center h-8 px-3 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">{s}</li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(job.related) && job.related.length > 0 && (
        <section className="mt-8 border-t border-slate-200 pt-6">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            More openings at {c.short_name || c.name || "this company"}
          </h2>
          <ul className="space-y-2">
            {job.related.map((r) => (
              <li key={r.slug}>
                <Link to={`/private-jobs/${r.company_slug}/${r.slug}`} className="text-orange-600 font-semibold hover:underline">
                  {r.title}
                </Link>
                {r.location && <span className="text-slate-500 text-sm"> — {r.location}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4">
        Listing curated by the hireds.in editorial team. hireds.in is not affiliated with {c.name || "this employer"}. Please verify job details on the <a href={job.apply_url || c.careers_url || c.website || "#"} target="_blank" rel="noopener noreferrer nofollow" className="text-orange-600 font-semibold">official careers page</a> before applying.
      </p>

      {/* Smart interlinks — 5-8 deep-links to related hubs */}
      <SmartInterlinks job={job} variant="private" />
    </div>
  );
}

// -----------------------------------------------------------
// PRIVATE COMPANY SALARY INSIGHTS  (/private-jobs/:companySlug/salaries)
// -----------------------------------------------------------
export function PrivateCompanySalaries() {
  const { companySlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/private-companies/${companySlug}/salaries`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [companySlug]);

  useNoindexOnNotFound(error);

  // Dataset + FAQPage JSON-LD injection (SEO link-magnet)
  useEffect(() => {
    if (!data || !data.company) return;
    const c = data.company;
    const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
    const overall = data.overall || {};
    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: `${c.name} salary data — ${data.role_count} live openings`,
      description: `Aggregated monthly salary ranges across ${data.role_count} live ${c.name} openings, grouped by experience band.`,
      url: `${SITE_URL}/private-jobs/${c.slug}/salaries`,
      keywords: [`${c.name} salary`, `${c.short_name || c.name} freshers salary`, "India private sector salary"],
      creator: { "@type": "Organization", name: "hireds.in", url: SITE_URL },
      isAccessibleForFree: true,
      license: "https://creativecommons.org/licenses/by/4.0/",
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-jsonld-dataset", "1");
    s.text = JSON.stringify(dataset);
    document.head.appendChild(s);
    return () => document.querySelectorAll('script[data-jsonld-dataset]').forEach((x) => x.remove());
  }, [data]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold text-slate-900">Salary data not available</h1>
        <p className="mt-2 text-slate-600">Browse all <Link to="/private-jobs" className="text-orange-600 font-semibold">private-sector openings →</Link></p>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Loading salary insights…</div>;

  const c = data.company;
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const overall = data.overall || {};
  const bands = data.bands || [];
  const roles = data.roles || [];
  const maxSalary = Math.max(...bands.map((b) => b.max || 0), 1);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="private-company-salaries">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Private Jobs", to: "/private-jobs" },
          { label: c.short_name || c.name, to: `/private-jobs/${c.slug}` },
          { label: "Salaries" },
        ]}
      />

      <header
        className="mt-4 mb-8 rounded-xl overflow-hidden p-6 md:p-10"
        style={{ background: c.brand_bg || "#0f172a", color: c.brand_color || "#fff" }}
      >
        <div className="text-xs uppercase tracking-wider font-bold opacity-90">
          Salary Insights · {data.role_count} live openings
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold mt-2 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {c.name} Salaries in India 2026
        </h1>
        <p className="mt-3 text-sm md:text-base opacity-95 max-w-3xl leading-relaxed">
          Monthly in-hand salary ranges at {c.name} across freshers, mid-level and senior roles based on <strong>{data.role_count}</strong> live openings on hireds.in.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
          {[
            { label: "Min /mo", val: overall.min },
            { label: "Average /mo", val: overall.avg },
            { label: "Max /mo", val: overall.max },
          ].map((k) => (
            <div key={k.label} className="bg-white/15 rounded-md px-3 py-2 text-center">
              <div className="text-lg md:text-2xl font-extrabold">{inr(k.val)}</div>
              <div className="text-[10px] uppercase tracking-wider opacity-80">{k.label}</div>
            </div>
          ))}
        </div>
      </header>

      {/* Salary bands bar chart (CSS-only, CLS-safe) */}
      {bands.length > 0 && (
        <section aria-labelledby="salary-bands-h2" className="mb-10" data-testid="salary-bands">
          <h2 id="salary-bands-h2" className="text-2xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Salary by experience band
          </h2>
          <div className="space-y-4">
            {bands.map((b) => {
              const pctMin = (b.min / maxSalary) * 100;
              const pctAvg = (b.avg / maxSalary) * 100;
              const pctMax = (b.max / maxSalary) * 100;
              return (
                <div key={b.key} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`salary-band-${b.key}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <div>
                      <div className="font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>{b.label}</div>
                      <div className="text-xs text-slate-500">{b.count} live openings</div>
                    </div>
                    <div className="text-sm text-slate-700 font-semibold">
                      {inr(b.min)} <span className="text-slate-400">→</span> {inr(b.max)} <span className="text-slate-400 text-xs">(avg {inr(b.avg)})</span>
                    </div>
                  </div>
                  {/* CSS bar chart */}
                  <div
                    className="relative h-3 bg-slate-100 rounded-full overflow-hidden"
                    role="img"
                    aria-label={`Salary range from ${inr(b.min)} to ${inr(b.max)} with average ${inr(b.avg)}`}
                  >
                    <div
                      className="absolute h-full rounded-full"
                      style={{ left: `${pctMin}%`, width: `${Math.max(pctMax - pctMin, 2)}%`, background: c.brand_bg || "#0f172a" }}
                    />
                    <div
                      className="absolute top-0 h-full w-0.5 bg-white shadow"
                      style={{ left: `${pctAvg}%` }}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Full roles table */}
      {roles.length > 0 && (
        <section aria-labelledby="salary-roles-h2" className="mb-10" data-testid="salary-roles-table">
          <h2 id="salary-roles-h2" className="text-2xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            Every {c.short_name || c.name} opening &amp; its salary
          </h2>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Role</th>
                  <th className="text-left px-4 py-2 font-semibold hidden sm:table-cell">Location</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Experience</th>
                  <th className="text-right px-4 py-2 font-semibold">Monthly range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {roles.map((r) => (
                  <tr key={r.slug} className="hover:bg-orange-50/50">
                    <td className="px-4 py-3">
                      <Link to={r.url} className="text-orange-600 font-semibold hover:underline">{r.title}</Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{r.location}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{r.experience}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">
                      {inr(r.salary_min)} – {inr(r.salary_max)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4">
        Data source: hireds.in curated listings only, published under <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="underline">CC BY 4.0</a>. hireds.in is not affiliated with {c.name}. For binding offer details, always refer to the official {c.name} careers portal.
      </p>
    </div>
  );
}

// -----------------------------------------------------------
// Compact reusable job row (shared between index + company detail)
// -----------------------------------------------------------
function PrivateJobRow({ job }) {
  const c = job.company || {};
  return (
    <Link
      to={`/private-jobs/${job.company_slug}/${job.slug}`}
      data-testid={`private-job-row-${job.slug}`}
      className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-md grid place-items-center text-white font-extrabold text-sm shrink-0"
          style={{ background: c.brand_bg || "#0f172a", color: c.brand_color || "#fff" }}
          aria-hidden="true"
        >
          {(c.short_name || c.name || "?").slice(0, 3).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-slate-900 text-base leading-snug truncate" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            {job.title}
          </h3>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
            {c.name && <span className="font-semibold">{c.short_name || c.name}</span>}
            {job.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</span>}
            {job.experience && <span>{job.experience}</span>}
            {job.salary_min ? <span>₹{job.salary_min.toLocaleString("en-IN")}–{(job.salary_max || job.salary_min).toLocaleString("en-IN")}/mo</span> : null}
          </div>
          {job.last_date && (
            <div className="mt-1 text-xs text-orange-700 font-semibold">
              Apply by {fmtDate(job.last_date)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
